/**
 * @module cercaliagl/control/Buildings3D
 */

import { VectorTile } from '@mapbox/vector-tile';
import Protobuf from 'pbf';
import { loadPlugin, PLUGINS_URL } from '../plugins';
import MapEventType from '../MapEventType';
import Control from './Control';
import MapControls from './MapControls';
import { LB_DOMAIN } from '../globals';
import { i18n } from '../dictionary';
import VControlButton from '../view/VControlButton';
import VShadowBuildings, { EventType } from '../view/VShadowBuildings';

/**
 * @classdesc
 * <b>Javascript ES5</b>
 * ```js
 * const control = new cercaliagl.control.Buildings3D();
 * ```
 * <br/>
 * This control enables <code>buildings3d</code> layer. It displays 3D buildings provided by MVT from server.
 * If base layer is a PNG raster. This buildings are provided by other `VectorTiles` server.
 * <br/><br/>
 * When this control is loaded an additional library is loaded `deckgl-plugin.min.js` which draws buildings
 * shadows and user can change the sun time modifying date and time via a UI component shown in the bottom right
 * corner
 *
 * @extends {Control}
 * @api
 */
class Buildings3D extends Control {
  /**
   * @constructor
   */
  constructor() {
    const controlButton = new VControlButton('city');
    const element = controlButton.getElement();
    const shadowBuildingsDialog = new VShadowBuildings();

    super({
      element,
      className: 'cercaliagl.control.Buildings3D',
      name: MapControls.BUILDINGS3D,
      dialog: shadowBuildingsDialog.getElement(),
    });

    /**
     * @private
     * @type {VShadowBuildings}
     */
    this.shadowBuildingsDialog_ = shadowBuildingsDialog;

    /**
     * @private
     * @type {VControlButton}
     */
    this.controlButton_ = controlButton;

    /**
     * @private
     * @type {HTMLElement}
     */
    this.element_ = element;

    /**
     * @private
     * @type {boolean}
     */
    this.enable_ = false;

    /**
     * @private
     * @type {SunLight}
     */
    this.sunLight_ = null;

    /**
     * @private
     * @type {LightingEffectç}
     */
    this.lightingEffect_ = null;

    /**
     * @private
     * @type {Deck}
     */
    this.deck_ = null;

    /**
     * @private
     * @type {string}
     */
    this.lastBounds_ = null;

    /**
     * @private
     * @type {window.DeckGL.TileLayer}
     */
    this.deckglLayers_ = [];

    this.initialize_();
  }

  /**
   * @private
   */
  initialize_() {
    this.controlButton_.setTooltip(i18n('tooltipBuildings3D'));
    this.element_.addEventListener('click', this.handleClick_.bind(this));

    loadPlugin(PLUGINS_URL.deckgl, 'deckgl').then(() => {
      this.shadowBuildingsDialog_.on(
        EventType.CHANGE,
        /** @param {import('../view/VShadowBuildings').ShadowBuildingsOptions} data Data */ (data) => {
          const date = new Date(data.date);
          const [hour, minutes] = data.hourmins.split(':');
          this.changeSunLightTime(parseInt(hour, 10), parseInt(minutes, 10), date.getDate(), date.getMonth() + 1, date.getFullYear());
        },
      );

      this.shadowBuildingsDialog_.on(EventType.DISABLE, () => {
        this.toggleLayer_('deckglmap', false);
        this.toggleLayer_('3dlandmarks', true);
      });
      this.shadowBuildingsDialog_.on(EventType.ENABLE, () => {
        this.initializeDeckGL_();
        this.toggleLayer_('3dlandmarks', false);
        this.toggleLayer_('deckglmap', true);
      });

      this.map_.on(MapEventType.BASELAYER_CHANGE, () => {
        this.map_.once('style.load', () => {
          this.clearLayers_();
          this.setLayers_();
          // this.updateLayers_();
          this.shadowBuildingsDialog_.toggle(false);
        });
      });
    });
  }

  /**
   * @private
   * @param {string} layerId Layer id
   * @param {boolean} visible Visible or not
   */
  toggleLayer_(layerId, visible) {
    if (this.map_.getLayer(layerId)) {
      this.map_.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
    }
  }

  /**
   * @private
   */
  handleClick_() {
    const cercaliaMap = this.getCercaliaMap();
    cercaliaMap.showBuildings3D(!this.enable_);
  }

  /**
   * Enables the visualComponents of 3dBuilding
   */
  enable3dBuildingInterface() {
    this.enable_ = true;
    this.controlButton_.enable();
    this.shadowBuildingsDialog_.show();
  }

  /**
   * Disables the visualComponents of 3dBuilding
   */
  disable3dBuildingInterface() {
    this.enable_ = false;
    this.controlButton_.disable();
    this.shadowBuildingsDialog_.hide();
    this.toggleLayer_('deckglmap', false);
    this.shadowBuildingsDialog_.toggle(false);
  }

  /**
   * @private
   */
  initializeDeckGL_() {
    if (!this.deck_) {
      this.sunLight_ = new window.DeckGL.SunLight({
        timestamp: new Date(),
        color: [255, 255, 255],
        intensity: 1,
        _shadow: true,
      });
      this.lightingEffect_ = new window.DeckGL.LightingEffect({
        ambientLight: new window.DeckGL.AmbientLight({
          color: [255, 255, 255],
          intensity: 1,
        }),
        sunLight: this.sunLight_,
      });
      this.lightingEffect_.shadowColor = [0, 0, 0, 0.4];

      this.deck_ = new window.DeckGL.Deck({
        gl: this.getMap().painter.context.gl,
        effects: [this.lightingEffect_],
        layers: [],
      });
      this.setLayers_();
    }
  }

  /**
   * @private
   */
  setLayers_() {
    const cercaliaMap = this.map_.getCercaliaMap();
    if (this.deckglLayers_.length === 0) {
      this.deckglLayers_ = [
        new window.DeckGL.TileLayer({
          id: 'deckglmap',
          stroked: false,
          filled: true,
          extruded: true,
          getElevation: (f) => f.properties.render_height || f.properties.height,
          getFillColor: (f) => (f.properties.color === 'transparent' ? [0, 0, 0, 0] : [210, 210, 210]),
          getLineWidth: 0,
          getRadius: 0,
          getTileData: ({
            x, y, z, bbox,
          }) => {
            const fromTegola = !!this.map_.getSource('buildings3d');
            let retVal = [];
            if (z >= 15) {
              if (!fromTegola) {
                const strBounds = cercaliaMap.getBounds().toString();
                if (this.lastBounds_ !== strBounds) {
                  const features = this.map_.queryRenderedFeatures({ layers: ['buildings3d'] });
                  retVal = Buildings3D.addLandFeature(features, bbox, fromTegola);
                  retVal = features;
                  this.lastBounds_ = strBounds;
                } else {
                  retVal = Buildings3D.addLandFeature([], bbox, fromTegola);
                }
              } else {
                retVal = fetch(`${LB_DOMAIN}/maps/buildings/${z}/${x}/${y}.vector.pbf`)
                  .then((response) => response.arrayBuffer())
                  .then((buffer) => {
                    const tile = new VectorTile(new Protobuf(buffer));
                    const features = [];
                    Object.keys(tile.layers).forEach((layerName) => {
                      const vectorTileLayer = tile.layers[layerName];
                      for (let i = 0; i < vectorTileLayer.length; i += 1) {
                        const vectorTileFeature = vectorTileLayer.feature(i);
                        const feature = vectorTileFeature.toGeoJSON(x, y, z);
                        features.push(feature);
                      }
                    });
                    Buildings3D.addLandFeature(features, bbox, fromTegola);
                    return features;
                  });
              }
            }
            return retVal;
          },
        }),
      ];
    }
    this.updateLayers_();
  }

  /**
   * @static
   * @param {Array.<Object>} features Features
   * @param {Object.<string, number>} bbox Bounding box
   * @param {boolean} fromTegola Buildings from tegola
   * @return {Array.<Object>}
   */
  static addLandFeature(features, bbox, fromTegola) {
    features.push({
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [
          [
            [bbox.west, bbox.north],
            [bbox.east, bbox.north],
            [bbox.east, bbox.south],
            [bbox.west, bbox.south],
          ],
        ],
      },
      properties: {
        [fromTegola ? 'height' : 'render_height']: 0,
        [fromTegola ? 'min_height' : 'render_min_height']: 0,
        color: 'transparent',
      },
    });
    return features;
  }

  /**
   * @private
   */
  clearLayers_() {
    if (this.deck_) {
      this.deck_.setProps({ layers: [] });
      this.deckglLayers_ = [];
    }
  }

  /**
   * @private
   */
  updateLayers_() {
    if (this.deck_) {
      this.deck_.setProps({ layers: this.deckglLayers_ });
      this.map_.addLayer(new window.DeckGL.MapboxLayer({ id: 'deckglmap', deck: this.deck_ }));
    }
  }

  /**
   * Change sun light time. It only works when `sun light` is enabled in UI component.
   * @api
   * @param {number} hour Hours `0` to `24`.
   * @param {number} minutes Minutes `0` to `60`
   * @param {number} dd Day `1` to `31`.
   * @param {number} mm Month. `0` to `11`.
   * @param {number} yyyy Year.
   */
  changeSunLightTime(hour, minutes, dd, mm, yyyy) {
    if (this.sunLight_) {
      const date = new Date(Number(yyyy), Number(mm), Number(dd), Number(hour), Number(minutes), 0);
      const dateAux = new Date(Number(yyyy), Number(mm), Number(dd), 16);
      this.sunLight_.timestamp = this.timeLimit_(date, dateAux);
      this.deck_.redraw(true);
    }
  }

  /**
   * @private
   * @param {Date} date Date
   * @param {Date} dateAux Date aux
   * @return {Date}
   */
  timeLimit_(date, dateAux) {
    const map = this.getMap();
    const oneMinuteMS = 60000;
    const times = window.DeckGL.SunCalc.getTimes(dateAux, map.getBounds()._sw.lat, this.map_.getBounds()._sw.lng);
    const actualTime = date.getTime();
    const sunsetTime = times.sunsetStart.getTime() - oneMinuteMS * 10;
    const sunriseTime = times.sunrise.getTime() + oneMinuteMS * 10;
    if (actualTime > sunsetTime) {
      return sunsetTime - oneMinuteMS * 10;
    }
    if (actualTime < sunriseTime) {
      return sunriseTime + oneMinuteMS * 10;
    }
    return date;
  }
}

export default Buildings3D;
